import { useEffect, useMemo, useRef, useState } from "react";
import { selectOrificeWithParallel } from "../calc/orifice";
import { API_526_ORIFICES, IN2_TO_MM2 } from "../data/api526";
import { GasSizingPanel } from "./GasSizingPanel";
import { SteamSizingPanel } from "./SteamSizingPanel";
import { LiquidSizingPanel } from "./LiquidSizingPanel";
import {
  TwoPhaseSizingPanel,
  type TwoPhaseInletKind,
} from "./TwoPhaseSizingPanel";
import { SubcooledSizingPanel } from "./SubcooledSizingPanel";
import { CaseWorkflowPanel } from "./CaseWorkflowPanel";
import { Help } from "./formBits";
import { SummaryPanel } from "./SummaryPanel";
import { CalcSheet, type SheetInclude } from "./CalcSheet";
import { ManualPanel } from "./ManualPanel";
import { PipingPanel } from "./PipingPanel";
import { FlarePanel } from "./FlarePanel";
import { FlareRadiationPanel } from "./FlareRadiationPanel";
import { KoshaFlareBsPanel } from "./KoshaFlareBsPanel";
import { ExplosiveLimitsPanel } from "./ExplosiveLimitsPanel";
import { FlowsheetPanel } from "./FlowsheetPanel";
import {
  convertFlowsheetPressures,
  newFlowsheetState,
  type FlowsheetState,
} from "./flowsheetState";
import {
  convertFlarePressures,
  newFlareState,
  type FlareState,
} from "./flareState";
import {
  convertPipingPressures,
  newPipingState,
  type PipingState,
} from "./pipingState";
import {
  convertCasePressures,
  applyBackpressureByTag,
  initialCasesIn,
  newCaseIn,
  removeCaseFrom,
  type CaseState,
} from "./caseState";
import { SaveBar, type SaveStatus } from "./SaveBar";
import {
  loadFromBrowser,
  loadPipingFromBrowser,
  loadFlareFromBrowser,
  loadFlowsheetFromBrowser,
  saveFlareToBrowser,
  savePipingToBrowser,
  saveFlowsheetToBrowser,
  loadUnitFromBrowser,
  saveToBrowser,
  saveUnitToBrowser,
} from "./persistence";
import {
  PRESSURE_UNITS,
  PRESSURE_UNIT_KEYS,
  type PressureUnit,
} from "./pressureUnit";

/** 최상위 모드. */
type Mode =
  | "case"
  | "summary"
  | "sheet"
  | "piping"
  | "flare"
  | "flareRadiation"
  | "flareRadiationKosha"
  | "explosiveLimits"
  | "flowsheet"
  | "calculator"
  | "manual";

/** 유체 상태. */
type FluidKind = "gas" | "steam" | "liquid" | "two-phase";

const FLUID_TABS: ReadonlyArray<{
  readonly kind: FluidKind;
  readonly label: string;
  readonly ready: boolean;
}> = [
  { kind: "gas", label: "기체·증기", ready: true },
  { kind: "steam", label: "수증기", ready: true },
  { kind: "liquid", label: "액체", ready: true },
  { kind: "two-phase", label: "2상 유동", ready: true },
];

/**
 * 두 가지 모드로 나뉜다.
 *  - 케이스 검토 : 여러 과압 시나리오를 나란히 놓고 지배 케이스를 정해 밸브를 확정
 *  - 개별 계산기 : 유체 상태별 사이징식을 단독으로 확인
 */
export function App() {
  const [mode, setMode] = useState<Mode>("case");
  // 브라우저에 저장된 게 있으면 그것으로, 없으면 예시로 시작한다.
  // initialCases() 를 두 번 부르면 서로 다른 id 가 만들어지므로 한 번만 부른다.
  const [cases, setCases] = useState<CaseState[]>(
    () => loadFromBrowser() ?? initialCasesIn(loadUnitFromBrowser() ?? "kPa"),
  );
  const [activeId, setActiveId] = useState<string>(() => "");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ kind: "idle" });
  const [flareMessage, setFlareMessage] = useState<string | null>(null);
  // 계산서에 무엇을 실을지. 배관·플레어는 안 한 검토일 수 있으므로 기본은 끈다.
  const [sheetInclude, setSheetInclude] = useState<SheetInclude>({
    sizing: true,
    piping: false,
    flare: false,
  });
  const [fluid, setFluid] = useState<FluidKind>("gas");
  const [pressureUnit, setPressureUnit] = useState<PressureUnit>(
    () => loadUnitFromBrowser() ?? "kPa",
  );
  // 플레어 헤더도 별도 검토다. 한 번에 한 시나리오(동시 분출 조합)를 본다.
  const [flare, setFlare] = useState<FlareState>(
    () =>
      loadFlareFromBrowser() ??
      convertFlarePressures(
        newFlareState(),
        "kPa",
        loadUnitFromBrowser() ?? "kPa",
      ),
  );
  // 공정 흐름도도 독립된 검토다.
  const [flowsheet, setFlowsheet] = useState<FlowsheetState>(
    () =>
      loadFlowsheetFromBrowser() ??
      convertFlowsheetPressures(
        newFlowsheetState(),
        "kPa",
        loadUnitFromBrowser() ?? "kPa",
      ),
  );
  // 배관 검토는 밸브 목록과 독립된 별도 검토다(한 번에 한 계통만 본다).
  const [piping, setPiping] = useState<PipingState>(
    () =>
      loadPipingFromBrowser() ??
      // 저장된 게 없으면 기본값을 현재 단위로 환산해 시작한다.
      convertPipingPressures(
        newPipingState(),
        "kPa",
        loadUnitFromBrowser() ?? "kPa",
      ),
  );

  /**
   * 단위를 바꿀 때 입력값도 함께 환산한다.
   * 숫자를 그대로 두면 물리량이 조용히 달라진다(517 kPag → 517 barg).
   */
  const changeUnit = (next: PressureUnit) => {
    setCases((prev) => convertCasePressures(prev, pressureUnit, next));
    setPiping((prev) => convertPipingPressures(prev, pressureUnit, next));
    setFlare((prev) => convertFlarePressures(prev, pressureUnit, next));
    setFlowsheet((prev) => convertFlowsheetPressures(prev, pressureUnit, next));
    setPressureUnit(next);
    saveUnitToBrowser(next);
  };

  // 변경될 때마다 브라우저에 저장. 첫 렌더는 건너뛴다(불러온 값을 곧바로 덮지 않게).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const t = setTimeout(() => {
      setSaveStatus(
        saveToBrowser(cases)
          ? { kind: "saved", at: new Date() }
          : { kind: "unavailable" },
      );
    }, 400);
    return () => clearTimeout(t);
  }, [cases]);

  // 배관 검토도 같은 방식으로 자동 저장한다. 밸브 목록과 별도 키를 쓴다.
  const firstPipingRender = useRef(true);
  useEffect(() => {
    if (firstPipingRender.current) {
      firstPipingRender.current = false;
      return;
    }
    const t = setTimeout(() => savePipingToBrowser(piping), 400);
    return () => clearTimeout(t);
  }, [piping]);

  const firstFlareRender = useRef(true);
  useEffect(() => {
    if (firstFlareRender.current) {
      firstFlareRender.current = false;
      return;
    }
    const t = setTimeout(() => saveFlareToBrowser(flare), 400);
    return () => clearTimeout(t);
  }, [flare]);

  const firstFlowsheetRender = useRef(true);
  useEffect(() => {
    if (firstFlowsheetRender.current) {
      firstFlowsheetRender.current = false;
      return;
    }
    const t = setTimeout(() => saveFlowsheetToBrowser(flowsheet), 400);
    return () => clearTimeout(t);
  }, [flowsheet]);

  // 삭제·불러오기 등으로 활성 밸브가 사라지면 첫 번째로 돌아간다.
  const active = cases.find((c) => c.id === activeId) ?? cases[0];

  const addCase = () => {
    const created = newCaseIn(pressureUnit, `PSV-${101 + cases.length}`);
    setCases((prev) => [...prev, created]);
    setActiveId(created.id);
    setMode("case");
  };

  /**
   * 플레어 헤더에서 구한 최악 배압을 케이스 검토로 보낸다.
   *
   * 태그가 같은 밸브의 **모든 시나리오 행**에 넣는다. 어느 과압 시나리오가
   * 어느 동시 분출 조합에 대응하는지는 설계자만 알 수 있으므로, 가장
   * 보수적인 값을 일괄 적용하고 그 사실을 화면에서 밝힌다.
   *
   * 계산 계층은 kPa 로 말하고 화면은 사용자 단위로 보관하므로 여기서 환산한다.
   */
  const applyBackpressure = (
    entries: ReadonlyArray<{ tag: string; backpressureKpaGauge: number }>,
  ) => {
    // 계산은 순수 함수로 먼저 끝내고 한 번에 반영한다 — 상태 갱신 함수 안에서
    // 다른 상태를 바꾸면 StrictMode 에서 두 번 실행된다.
    const r = applyBackpressureByTag(cases, entries, pressureUnit);
    setCases(r.cases);
    setFlareMessage(
      r.appliedTags.length === 0
        ? "태그가 일치하는 밸브를 케이스 검토에서 찾지 못했습니다. 태그를 같게 맞춰 주십시오."
        : `${r.appliedTags.join(", ")} 의 배압을 채웠습니다. 케이스 검토에서 다시 사이징해 보십시오.`,
    );
  };

  const removeCase = (id: string) => {
    // 상태 갱신 함수 안에서 다른 상태를 바꾸면 StrictMode 에서 두 번 실행되어
    // 예상치 못한 결과가 난다. 계산은 순수 함수로 먼저 끝내고 한 번에 반영한다.
    const next = removeCaseFrom(cases, id, activeId);
    if (next === null) return;
    setCases(next.cases);
    setActiveId(next.activeId);
  };
  const [inlet, setInlet] = useState<TwoPhaseInletKind>("two-phase");
  const [areaInput, setAreaInput] = useState("3698");
  const [unit, setUnit] = useState<"mm2" | "in2">("mm2");

  const areaMm2 = useMemo(() => {
    const v = Number.parseFloat(areaInput);
    if (!Number.isFinite(v)) return Number.NaN;
    return unit === "mm2" ? v : v * IN2_TO_MM2;
  }, [areaInput, unit]);

  const result = useMemo(() => selectOrificeWithParallel(areaMm2), [areaMm2]);

  return (
    <div className="page">
      <header className="page__header">
        <h1>PSV Calculator</h1>
        <p className="subtitle">안전밸브 사이징 — API 520 / 521 / 526</p>
      </header>

      <p className="privacy" role="note">
        입력하신 값은 이 브라우저 안에서만 계산·저장되며 외부로 전송되지
        않습니다.
      </p>

      <SaveBar
        cases={cases}
        piping={piping}
        status={saveStatus}
        onImport={(next, nextPiping) => {
          setCases(next);
          // 배관 검토가 없는 예전 파일이면 화면의 값을 그대로 둔다.
          if (nextPiping) setPiping(nextPiping);
          setActiveId(next[0]?.id ?? "");
          setMode("case");
        }}
        onReset={() => {
          const fresh = initialCasesIn(pressureUnit);
          setCases(fresh);
          setPiping(
            convertPipingPressures(newPipingState(), "kPa", pressureUnit),
          );
          setActiveId(fresh[0]!.id);
          setSaveStatus({ kind: "idle" });
          setMode("case");
        }}
      />

      {/* 밸브 추가·삭제는 이 줄(모드 탭 높이)에 고정한다.
          밸브 목록 줄에 두면 밸브가 늘어날 때마다 버튼이 밀려 위치가 흔들린다. */}
      <nav className="tabs tabs--top" aria-label="모드">
        <div className="tabs__group">
        <button
          type="button"
          className={"tabs__btn" + (mode === "case" ? " tabs__btn--active" : "")}
          aria-current={mode === "case" ? "page" : undefined}
          onClick={() => setMode("case")}
        >
          케이스 검토
        </button>
        <button
          type="button"
          className={
            "tabs__btn" + (mode === "summary" ? " tabs__btn--active" : "")
          }
          aria-current={mode === "summary" ? "page" : undefined}
          onClick={() => setMode("summary")}
        >
          검토 요약
        </button>
        <button
          type="button"
          className={
            "tabs__btn" + (mode === "sheet" ? " tabs__btn--active" : "")
          }
          aria-current={mode === "sheet" ? "page" : undefined}
          onClick={() => setMode("sheet")}
        >
          계산서
        </button>
        <button
          type="button"
          className={
            "tabs__btn" + (mode === "piping" ? " tabs__btn--active" : "")
          }
          aria-current={mode === "piping" ? "page" : undefined}
          onClick={() => setMode("piping")}
        >
          배관 검토
        </button>
        <button
          type="button"
          className={
            "tabs__btn" + (mode === "flare" ? " tabs__btn--active" : "")
          }
          aria-current={mode === "flare" ? "page" : undefined}
          onClick={() => setMode("flare")}
        >
          플레어 헤더
        </button>
        <button
          type="button"
          className={
            "tabs__btn" + (mode === "flareRadiation" ? " tabs__btn--active" : "")
          }
          aria-current={mode === "flareRadiation" ? "page" : undefined}
          onClick={() => setMode("flareRadiation")}
        >
          플레어 복사열·높이 (API 521)
        </button>
        <button
          type="button"
          className={
            "tabs__btn" + (mode === "flareRadiationKosha" ? " tabs__btn--active" : "")
          }
          aria-current={mode === "flareRadiationKosha" ? "page" : undefined}
          onClick={() => setMode("flareRadiationKosha")}
        >
          플레어 복사열·높이 (KOSHA)
        </button>
        <button
          type="button"
          className={
            "tabs__btn" + (mode === "explosiveLimits" ? " tabs__btn--active" : "")
          }
          aria-current={mode === "explosiveLimits" ? "page" : undefined}
          onClick={() => setMode("explosiveLimits")}
        >
          혼합가스 폭발한계
        </button>
        <button
          type="button"
          className={
            "tabs__btn" + (mode === "flowsheet" ? " tabs__btn--active" : "")
          }
          aria-current={mode === "flowsheet" ? "page" : undefined}
          onClick={() => setMode("flowsheet")}
        >
          공정 흐름도
        </button>
        <button
          type="button"
          className={
            "tabs__btn" + (mode === "calculator" ? " tabs__btn--active" : "")
          }
          aria-current={mode === "calculator" ? "page" : undefined}
          onClick={() => setMode("calculator")}
        >
          개별 계산기
        </button>
        <button
          type="button"
          className={
            "tabs__btn" + (mode === "manual" ? " tabs__btn--active" : "")
          }
          aria-current={mode === "manual" ? "page" : undefined}
          onClick={() => setMode("manual")}
        >
          사용 설명서
        </button>
        </div>

        <div className="tabs__actions">
          <label className="unitpick">
            <span className="muted">압력 단위</span>
            <select
              value={pressureUnit}
              onChange={(e) => changeUnit(e.target.value as PressureUnit)}
              title="입력한 값도 함께 환산됩니다. 계산은 항상 kPa 로 수행합니다."
            >
              {PRESSURE_UNIT_KEYS.map((u) => (
                <option key={u} value={u}>
                  {PRESSURE_UNITS[u].label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn" onClick={addCase}>
            + 밸브 추가
          </button>
          {/* 지울 수 없을 때도 자리를 유지한다 — 버튼이 나타났다 사라지면
              옆 버튼 위치가 흔들려 오조작을 부른다. */}
          <button
            type="button"
            className="btn btn--ghost"
            disabled={mode !== "case" || !active || cases.length <= 1}
            title={
              cases.length <= 1
                ? "마지막 밸브는 삭제할 수 없습니다"
                : mode !== "case"
                  ? "케이스 검토 탭에서 삭제할 수 있습니다"
                  : `${active?.tag ?? ""} 삭제`
            }
            onClick={() => active && removeCase(active.id)}
          >
            밸브 삭제
          </button>
        </div>
      </nav>

      {(mode === "case" || mode === "summary") && (
        <nav className="tabs tabs--sub" aria-label="검토 밸브">
          {cases.map((c) => (
            <button
              key={c.id}
              type="button"
              className={
                "tabs__btn" +
                (mode === "case" && c.id === active?.id
                  ? " tabs__btn--active"
                  : "")
              }
              onClick={() => {
                setActiveId(c.id);
                setMode("case");
              }}
            >
              {c.tag || "(태그 없음)"}
            </button>
          ))}
        </nav>
      )}

      {mode === "case" &&
        (active ? (
          <CaseWorkflowPanel
            value={active}
            unit={pressureUnit}
            onChange={(next) =>
              setCases((prev) =>
                prev.map((c) => (c.id === next.id ? next : c)),
              )
            }
          />
        ) : (
          <section className="card">
            <p className="hint">밸브를 추가하십시오.</p>
          </section>
        ))}

      {mode === "summary" && <SummaryPanel cases={cases} unit={pressureUnit} />}

      {mode === "sheet" && (
        <CalcSheet
          cases={cases}
          unit={pressureUnit}
          piping={piping}
          flare={flare}
          include={sheetInclude}
          onIncludeChange={setSheetInclude}
        />
      )}

      {mode === "piping" && (
        <PipingPanel
          value={piping}
          unit={pressureUnit}
          onChange={setPiping}
        />
      )}

      {mode === "flare" && flareMessage && (
        <section className="card">
          <p className="hint" role="status">
            {flareMessage}
          </p>
        </section>
      )}

      {mode === "flare" && (
        <FlarePanel
          value={flare}
          unit={pressureUnit}
          onChange={setFlare}
          onApplyBackpressure={applyBackpressure}
        />
      )}

      {mode === "flareRadiation" && <FlareRadiationPanel />}
      {mode === "flareRadiationKosha" && <KoshaFlareBsPanel />}
      {mode === "explosiveLimits" && <ExplosiveLimitsPanel />}

      {mode === "flowsheet" && (
        <FlowsheetPanel
          value={flowsheet}
          unit={pressureUnit}
          onChange={setFlowsheet}
        />
      )}

      {mode === "manual" && <ManualPanel />}

      {mode === "calculator" && (
        <>
      <nav className="tabs tabs--sub" aria-label="유체 상태">
        {FLUID_TABS.map((t) => (
          <button
            key={t.kind}
            type="button"
            className={
              "tabs__btn" + (fluid === t.kind ? " tabs__btn--active" : "")
            }
            disabled={!t.ready}
            aria-current={fluid === t.kind ? "page" : undefined}
            onClick={() => setFluid(t.kind)}
          >
            {t.label}
            {!t.ready && <span className="tabs__soon">준비 중</span>}
          </button>
        ))}
      </nav>

      {fluid === "gas" && <GasSizingPanel />}
      {fluid === "steam" && <SteamSizingPanel />}
      {fluid === "liquid" && <LiquidSizingPanel />}
      {fluid === "two-phase" && (
        <>
          <nav className="tabs tabs--sub" aria-label="PRV 입구 조건">
            <button
              type="button"
              className={
                "tabs__btn" +
                (inlet === "two-phase" ? " tabs__btn--active" : "")
              }
              onClick={() => setInlet("two-phase")}
            >
              입구가 2상 (C.2.2)
            </button>
            <button
              type="button"
              className={
                "tabs__btn" + (inlet === "subcooled" ? " tabs__btn--active" : "")
              }
              onClick={() => setInlet("subcooled")}
            >
              입구가 과냉 액체 (C.2.3)
            </button>
          </nav>
          {inlet === "two-phase" ? (
            <TwoPhaseSizingPanel />
          ) : (
            <SubcooledSizingPanel />
          )}
        </>
      )}

      <section className="card">
        <h2>오리피스 선정 (면적 직접 입력)</h2>
        <p className="hint">
          API 520 사이징식으로 구한 소요 유효 오리피스 면적을 입력하면, API 526
          표준 규격 중 이를 만족하는 최소 오리피스를 선정합니다.
        </p>

        <div className="field">
          <label htmlFor="area">
            소요 유효 오리피스 면적
            <Help topic="requiredArea" />
          </label>
          <div className="input-row">
            <input
              id="area"
              type="text"
              inputMode="decimal"
              value={areaInput}
              onChange={(e) => setAreaInput(e.target.value)}
              aria-describedby="area-help"
            />
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value as "mm2" | "in2")}
              aria-label="면적 단위"
              title="입력한 면적의 단위입니다."
            >
              <option value="mm2">mm²</option>
              <option value="in2">in²</option>
            </select>
          </div>
          <p id="area-help" className="hint">
            예시는 API 520 Part I 10판 5.6.3.2 예제의 3698 mm² (5.73 in²)입니다.
          </p>
        </div>

        {result.ok ? (
          <div className="result result--ok">
            <div className="result__headline">
              <span className="result__label">선정 오리피스</span>
              <strong className="result__value">
                {result.orifice.designation}
              </strong>
              {result.valveCount > 1 && (
                <span className="badge">{result.valveCount}개 병렬</span>
              )}
            </div>

            <dl className="result__grid">
              <div>
                <dt>유효 면적</dt>
                <dd>
                  {result.orifice.effectiveAreaMm2.toLocaleString("ko-KR", {
                    maximumFractionDigits: 1,
                  })}{" "}
                  mm²
                  <span className="muted">
                    {" "}
                    ({result.orifice.effectiveAreaIn2} in²)
                  </span>
                </dd>
              </div>
              <div>
                <dt>소요 면적</dt>
                <dd>
                  {result.areaPerValveMm2.toLocaleString("ko-KR", {
                    maximumFractionDigits: 1,
                  })}{" "}
                  mm²
                  {result.valveCount > 1 && (
                    <span className="muted"> (밸브 1개당)</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>여유율</dt>
                <dd>{result.marginPct.toFixed(1)} %</dd>
              </div>
            </dl>

            <h3>제공 밸브 규격 (API 526 Table 2~15)</h3>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>규격</th>
                    <th>입구</th>
                    <th>출구</th>
                    <th>입구 클래스</th>
                    <th>출구 클래스</th>
                  </tr>
                </thead>
                <tbody>
                  {result.orifice.valveSizes.map((v, i) => (
                    <tr key={`${v.size}-${v.inletClass}-${i}`}>
                      <td className="mono">{v.size}</td>
                      <td>{v.inletNps}&quot;</td>
                      <td>{v.outletNps}&quot;</td>
                      <td>{v.inletClass}</td>
                      <td>{v.outletClass}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="result result--error" role="alert">
            {result.message}
          </div>
        )}
      </section>

      <section className="card">
        <h2>표준 오리피스 일람 (API 526)</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>문자</th>
                <th>면적 (mm²)</th>
                <th>면적 (in²)</th>
                <th>밸브 규격</th>
              </tr>
            </thead>
            <tbody>
              {API_526_ORIFICES.map((o) => (
                <tr
                  key={o.designation}
                  className={
                    result.ok && result.orifice.designation === o.designation
                      ? "row--selected"
                      : undefined
                  }
                >
                  <td className="mono">{o.designation}</td>
                  <td>
                    {o.effectiveAreaMm2.toLocaleString("ko-KR", {
                      maximumFractionDigits: 1,
                    })}
                  </td>
                  <td>{o.effectiveAreaIn2}</td>
                  <td className="mono muted">
                    {[...new Set(o.valveSizes.map((v) => v.size))].join(", ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
        </>
      )}

      <footer className="page__footer">
        <p>
          출처: API Standard 526 4판(1995) Table 1 · Table 2~15. 면적값은 API 520
          Part I 10판(2020) 예제 3건과 교차검증 완료.
        </p>
      </footer>
    </div>
  );
}
